import { css,keyframes } from '@emotion/css';
import { usePreviewContainer } from 'Shared/frontend/PreviewProvider';
import { scrollableAncestor } from 'Shared/frontend/scrollableAncestor';
import { theme, sizes } from 'Shared/frontend/SettingsProvider';
import { onCleanup, onMount } from 'solid-js';
import { Portal } from 'solid-js/web';

const overlayStyle = (isPreview:boolean) => css(theme().root, {
	height: isPreview ? '100%' : '100vh',
	width: isPreview ? '100%' : '100cqw',
	position: isPreview ? 'absolute' : 'fixed',
	top: 0,
	left: 0,
	zIndex: 10,
});

const backgroundStyle = () => css({
	width: '100%',
	height: '100%',
	backgroundColor: 'rgba(0, 0, 0, 0.8)',
});

const slideIn = (contentWidth:number) => keyframes`
	from {left: calc( -100cqw +  min((100cqw - ${contentWidth}) / 2, 0px)) } 
	to {left: 0;}
`;

const fadeIn = () => keyframes`
	from {filter: opacity(0) } 
	to {filter: opacity(1)}
`;

const foregroundStyle = (animation:string|unknown, isPreview:boolean) => css({
	position: 'absolute',
	width: '100%',
	top: 0,
	overflow: 'auto',
	maxHeight: isPreview ? '100%' : '100vh',
	...(animation ? {animation: animation} : {}),

	[`@container (min-width:${sizes().flipToMobileNav}px)`]: {
		paddingTop: '2em' ,
		paddingBottom: '2em' 
	},
});

const bodyStyle = () => css({
	margin: '0 auto',
	width: 'fit-content',
});

const closeButtonBarStyle = () => css({
	position: 'sticky',
	top: 0,
	right: 0,
	height: 0,
	opacity: 0.95,
	zIndex: 30,
});

const closeIconStyle = () => css({
	...theme().overlay.button,
	position: 'absolute',
	top: 0,
	right: 0,
});

/* Styling suitable for use over an image */
const closeIconOverImageStyle = () => css({
	background: '#0000003d',
	borderRadius: '50%',
	color: '#ffffff',
	margin: 3,
});



export function Overlay(P)
{
	/* Warning: only supports a single overlay. Otherwise cf an 'overlayed' count. cf <Portal> */

//TODO pass slideIn contentWidth in here, or better automate the calculation. cf making bodyStyle an absolute and setting right to 0 or something. Or just leave as 0.

	/* Allows the backend to place overlays over preview panes: */
	const previewContainer = usePreviewContainer();
	const mount = () => previewContainer?.();

	const animation = () => {
		if (P.animation == 'fadeIn')
			return `${fadeIn()} 1s`;
		if (P.animation == 'slideIn')
			return `${slideIn('0px')} 0.5s`;
		return undefined;
	};

	/* Prevent underlay from scrolling */
	onMount(() => {
		const previewEl = mount();
		if (previewEl) {
			/* In 2-column mode the right pane scrolls independently; lock it.
			   In single-column mode there is no scrollable ancestor, so skip the lock. */
			const scrollEl = scrollableAncestor(previewEl);
			if (scrollEl) {
				scrollEl.style.overflowY = 'hidden';
				onCleanup(() => { scrollEl.style.overflowY = ''; });
			}
		} else {
			document.body.style.overflowY = 'hidden';
			onCleanup(() => { document.body.style.overflowY = ''; });
		}
	});

	return (
		<Portal mount={mount()}>
			<div class={overlayStyle(!!mount())}>
				<div class={backgroundStyle()} />
				<div class={foregroundStyle(animation(), !!mount())}>
					<div class={bodyStyle()}>	
						<div class={closeButtonBarStyle()}>
							<button onClick={P.close}
								classList={{
									[closeIconStyle()]: true,
									[closeIconOverImageStyle()]: P.iconOverImage
								}} 
							>X</button>
						</div>
						{P.children}
					</div>
				</div>
			</div>
		</Portal>
	);
}

